import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useAuth } from "../../../contexts/AuthContext";

type UserProfile = {
  id: number;
  name: string;
  email: string;
  role: "admin" | "manager" | "operator";
};

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  operator: "Operator",
};

const AccountPage: React.FC = () => {
  const { token, role } = useAuth();
  const api = process.env.REACT_APP_API_URL || "";
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Password change form
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${api}/auth/me`, { headers });
        if (!res.ok) throw new Error();
        setProfile(await res.json());
      } catch {
        showSnack("Failed to load profile", "error");
      } finally {
        setProfileLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  const pwValid =
    currentPw.length > 0 &&
    newPw.length >= 6 &&
    newPw === confirmPw;

  const handleChangePassword = async () => {
    if (!pwValid) return;
    setPwSaving(true);
    try {
      const res = await fetch(`${api}/auth/me/password`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      if (!res.ok && res.status !== 204) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to change password");
      }
      showSnack("Password changed successfully", "success");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e: any) {
      showSnack(e.message || "Failed to change password", "error");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 0, sm: 1 },
        maxWidth: { xs: "100%", sm: 520 },
        mx: { xs: 0, sm: "auto" },
      }}
    >
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mb: { xs: 2, sm: 3 } }}
      >
        My Account
      </Typography>

      {/* Profile info */}
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 2.5 }, mb: { xs: 3, sm: 4 }, borderRadius: 2 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Profile
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {profileLoading ? (
          <Box display="flex" justifyContent="center" py={2}>
            <CircularProgress size={24} />
          </Box>
        ) : profile ? (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary">Name</Typography>
              <Typography variant="body1" fontWeight={500}>{profile.name}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Email</Typography>
              <Typography variant="body1">{profile.email}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Role</Typography>
              <Box mt={0.5}>
                <Chip
                  label={ROLE_LABEL[profile.role] ?? profile.role}
                  size="small"
                  color={
                    profile.role === "admin"
                      ? "primary"
                      : profile.role === "manager"
                      ? "info"
                      : "default"
                  }
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <Typography color="error" variant="body2">Could not load profile.</Typography>
        )}
      </Paper>

      {/* Change password */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Change Password
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Current password"
            type={showCurrent ? "text" : "password"}
            fullWidth
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowCurrent((v) => !v)}>
                      {showCurrent ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="New password"
            type={showNew ? "text" : "password"}
            fullWidth
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            helperText="Minimum 6 characters"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowNew((v) => !v)}>
                      {showNew ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            label="Confirm new password"
            type="password"
            fullWidth
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            error={confirmPw.length > 0 && confirmPw !== newPw}
            helperText={confirmPw.length > 0 && confirmPw !== newPw ? "Passwords do not match" : ""}
          />
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={pwSaving || !pwValid}
            sx={{
              alignSelf: { xs: "stretch", sm: "flex-start" },
              py: { xs: 1.1, sm: 0.75 },
            }}
          >
            {pwSaving ? <CircularProgress size={20} /> : "Update Password"}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountPage;
